/*=========================================================
  HYPHEN JOURNEY
  - Page-flip (book turn) animation on tab click / arrows / keyboard
  - Mouse-drag + touch-swipe page turning (live follow + snap)
=========================================================*/

document.addEventListener("DOMContentLoaded", () => {

  const section = document.querySelector(".hyphen-journey");

  if (!section) return;

  /*=========================================================
      ELEMENTS
  =========================================================*/

  const image = section.querySelector(".journey-image");
  const preloadImage = section.querySelector(".journey-image-preload");
  const flip = section.querySelector(".journey-flip");
  const flipImage = section.querySelector(".journey-flip-image");
  const flipShade = section.querySelector(".journey-flip-shade");
  const paper = section.querySelector(".journey-paper");
  const previousButton = section.querySelector(".journey-prev");
  const nextButton = section.querySelector(".journey-next");
  const currentPageText = section.querySelector(".journey-current");
  const totalPageText = section.querySelector(".journey-total");
  const tabs = [...section.querySelectorAll(".journey-tab")];

  const json = JSON.parse(
      section.querySelector(".journey-data").textContent
  );

  /*=========================================================
      BUILD DATA
  =========================================================*/

  const grouped = {};

  json.journals.forEach(journal => {
      if (!grouped[journal.year]) {
          grouped[journal.year] = [];
      }
      grouped[journal.year].push(journal);
  });

  Object.keys(grouped).forEach(year => {
      grouped[year].sort((a, b) => a.journal_number - b.journal_number);
  });

  const years = Object.keys(grouped).sort();

  /*=========================================================
      STATE
  =========================================================*/

  const state = { year: 0, journal: 0, page: 0 };

  const FLIP_MAX = 172;       // degrees of the full page turn
  const FLIP_MS = 1200;       // page-turn speed in ms (higher = slower)
  const COMMIT_AT = 0.3;      // drag progress needed to complete a turn
  let isAnimating = false;    // guards overlapping turns
  let drag = null;            // active drag session

  /*=========================================================
      HELPERS
  =========================================================*/

  const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

  // Source url for a given {year, journal, page} state (null if missing).
  function pageSrc(s) {
      const year = years[s.year];
      const journals = grouped[year];
      const journal = journals && journals[s.journal];
      if (!journal || !journal.pages.length) return null;
      return journal.pages[clamp(s.page, 0, journal.pages.length - 1)];
  }

  // Next / previous target WITHIN the current year (null at the ends).
  function computeTarget(dir) {
      const year = years[state.year];
      const journals = grouped[year];
      const journal = journals && journals[state.journal];
      if (!journal) return null;

      const s = { year: state.year, journal: state.journal, page: state.page };

      if (dir === "next") {
          if (state.page < journal.pages.length - 1) { s.page++; return s; }
          if (state.journal < journals.length - 1) { s.journal++; s.page = 0; return s; }
          return null;
      }
      // prev
      if (state.page > 0) { s.page--; return s; }
      if (state.journal > 0) {
          s.journal--;
          s.page = journals[s.journal].pages.length - 1;
          return s;
      }
      return null;
  }

  function setFlipTransition(on) {
      flip.style.transition = on
          ? "transform " + FLIP_MS + "ms cubic-bezier(.33, 1, .68, 1)"
          : "none";
      if (flipShade) {
          flipShade.style.transition = on ? "opacity " + FLIP_MS + "ms ease" : "none";
      }
  }

  // Apply a rotation and matching shade for the current turn.
  function applyFlipAngle(angle) {
      flip.style.transform = "rotateY(" + angle + "deg)";
      if (flipShade) {
          flipShade.style.opacity = clamp(Math.abs(angle) / FLIP_MAX, 0, 1) * 0.9;
      }
  }

  // Prepare the flip layer for a turn in `dir` from `fromSrc` to `toSrc`.
  function armFlip(dir, fromSrc, toSrc) {
      flip.classList.add("is-active");
      image.style.opacity = "1";
      image.style.transform = "none";

      if (dir === "next") {
          // current page turns away, next page is revealed beneath
          flipImage.src = fromSrc;
          if (toSrc) image.src = toSrc;
          setFlipTransition(false);
          applyFlipAngle(0);
      } else {
          // previous page swings in from the spine, over the current page
          flipImage.src = toSrc || fromSrc;
          setFlipTransition(false);
          applyFlipAngle(-FLIP_MAX);
      }
      // force a reflow so the start transform is committed before transitioning
      void flip.offsetWidth;
  }

  // Run the callback once the flip transition ends (with a safety fallback).
  function finishAfter(cb) {
      let done = false;
      const handler = (e) => {
          if (e && e.propertyName && e.propertyName !== "transform") return;
          if (done) return;
          done = true;
          flip.removeEventListener("transitionend", handler);
          cb();
      };
      flip.addEventListener("transitionend", handler);
      setTimeout(handler, FLIP_MS + 300);
  }

  // Commit the turn: adopt the target state and reset the flip layer.
  function finalize(toState) {
      state.year = toState.year;
      state.journal = toState.journal;
      state.page = toState.page;

      const src = pageSrc(state);
      if (src) image.src = src;
      image.style.opacity = "1";
      image.style.transform = "none";

      resetFlip();
      isAnimating = false;

      const year = years[state.year];
      const journal = grouped[year][state.journal];
      updatePagination(year, journal);
      updateTabs(year);
      updateNavigation(year, journal);
      preloadNext(year, journal);
  }

  // Abort a turn and restore the on-screen page.
  function cancelTurn(fromSrc) {
      if (fromSrc) image.src = fromSrc;
      image.style.opacity = "1";
      image.style.transform = "none";
      resetFlip();
      isAnimating = false;
  }

  function resetFlip() {
      flip.classList.remove("is-active");
      setFlipTransition(false);
      applyFlipAngle(0);
      if (flipShade) flipShade.style.opacity = "0";
  }

  /*=========================================================
      ANIMATED TURN (tab click / arrows / keyboard)
  =========================================================*/

  function turnTo(toState, dir) {
      if (!toState || isAnimating) return;

      const fromSrc = pageSrc(state);
      const toSrc = pageSrc(toState);

      // If images are missing, switch instantly without animating.
      if (!fromSrc || !toSrc) { finalize(toState); return; }

      isAnimating = true;
      armFlip(dir, fromSrc, toSrc);

      setFlipTransition(true);
      applyFlipAngle(dir === "next" ? -FLIP_MAX : 0);

      finishAfter(() => finalize(toState));
  }

  /*=========================================================
      INITIALIZE
  =========================================================*/

  render();

  /*=========================================================
      YEAR TAB CLICK
  =========================================================*/

  tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
          if (isAnimating) return;

          const yearIndex = years.indexOf(tab.dataset.year);
          if (yearIndex === -1 || yearIndex === state.year) return;

          const dir = yearIndex > state.year ? "next" : "prev";
          turnTo({ year: yearIndex, journal: 0, page: 0 }, dir);
      });
  });

  /*=========================================================
      NEXT / PREVIOUS BUTTONS
  =========================================================*/

  nextButton.addEventListener("click", () => turnTo(computeTarget("next"), "next"));
  previousButton.addEventListener("click", () => turnTo(computeTarget("prev"), "prev"));

  /*=========================================================
      MOUSE DRAG + TOUCH SWIPE (unified via Pointer Events)
      Live-follows the drag and snaps forward/back on release.
  =========================================================*/

  paper.addEventListener("pointerdown", (e) => {
      if (isAnimating || e.button === 2) return;
      drag = {
          id: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          armed: false,
          dir: null,
          toState: null,
          fromSrc: null,
          progress: 0
      };
      window.addEventListener("pointermove", onDragMove);
      window.addEventListener("pointerup", onDragEnd);
      window.addEventListener("pointercancel", onDragEnd);
  });

  function onDragMove(e) {
      if (!drag || e.pointerId !== drag.id) return;

      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;

      if (!drag.armed) {
          // wait for a clear horizontal intent before hijacking the gesture
          if (Math.abs(dx) < 10) return;
          if (Math.abs(dy) > Math.abs(dx)) { endDragListeners(); drag = null; return; }

          const dir = dx < 0 ? "next" : "prev";
          const toState = computeTarget(dir);
          if (!toState) { endDragListeners(); drag = null; return; }

          const fromSrc = pageSrc(state);
          const toSrc = pageSrc(toState);
          if (!fromSrc || !toSrc) { endDragListeners(); drag = null; return; }

          drag.dir = dir;
          drag.toState = toState;
          drag.fromSrc = fromSrc;
          drag.armed = true;
          isAnimating = true;               // block other triggers mid-drag
          paper.classList.add("is-dragging");
          armFlip(dir, fromSrc, toSrc);
          try { paper.setPointerCapture(drag.id); } catch (err) {}
      }

      e.preventDefault();
      const width = paper.clientWidth || 1;
      const progress = clamp(Math.abs(dx) / width, 0, 1);
      drag.progress = progress;

      const angle = drag.dir === "next"
          ? -(progress * FLIP_MAX)
          : (-FLIP_MAX + progress * FLIP_MAX);
      applyFlipAngle(angle);
  }

  function onDragEnd() {
      if (!drag) { endDragListeners(); return; }

      if (!drag.armed) {
          // treated as a tap / non-horizontal gesture — nothing to do
          endDragListeners();
          drag = null;
          return;
      }

      const { dir, toState, fromSrc, progress } = drag;
      paper.classList.remove("is-dragging");
      try { paper.releasePointerCapture(drag.id); } catch (err) {}

      setFlipTransition(true);

      if (progress > COMMIT_AT) {
          applyFlipAngle(dir === "next" ? -FLIP_MAX : 0);
          finishAfter(() => finalize(toState));
      } else {
          // snap back to the start of the turn
          if (dir === "next") image.src = fromSrc;
          applyFlipAngle(dir === "next" ? 0 : -FLIP_MAX);
          finishAfter(() => cancelTurn(fromSrc));
      }

      endDragListeners();
      drag = null;
  }

  function endDragListeners() {
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", onDragEnd);
      window.removeEventListener("pointercancel", onDragEnd);
  }

  /*=========================================================
      KEYBOARD
  =========================================================*/

  document.addEventListener("keydown", (event) => {
      const tag = document.activeElement && document.activeElement.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      if (event.key === "ArrowRight") turnTo(computeTarget("next"), "next");
      if (event.key === "ArrowLeft") turnTo(computeTarget("prev"), "prev");
  });

  /*=========================================================
      RENDER (initial + state updates)
  =========================================================*/

  function render() {
      const year = years[state.year];
      const journal = grouped[year] && grouped[year][state.journal];

      if (!journal || !journal.pages.length) return;

      const page = journal.pages[state.page];

      image.src = page;
      image.alt = `Journey ${year} Journal ${journal.journal_number} Page ${state.page + 1}`;
      image.onload = () => {
          image.style.opacity = "1";
          image.style.transform = "none";
      };

      updatePagination(year, journal);
      updateTabs(year);
      updateNavigation(year, journal);
      preloadNext(year, journal);
  }

  function updatePagination(year, journal) {
      currentPageText.textContent = state.page + 1;
      totalPageText.textContent = journal.pages.length;
  }

  function updateTabs(currentYear) {
      tabs.forEach((tab) => {
          tab.classList.toggle("is-active", tab.dataset.year === currentYear);
      });
  }

  function updateNavigation(year, journal) {
      const journals = grouped[year];
      const first = state.journal === 0 && state.page === 0;
      const last =
          state.journal === journals.length - 1 &&
          state.page === journal.pages.length - 1;

      previousButton.classList.toggle("disabled", first);
      nextButton.classList.toggle("disabled", last);
  }

  function preloadNext(year, journal) {
      let nextSrc = null;

      if (state.page + 1 < journal.pages.length) {
          nextSrc = journal.pages[state.page + 1];
      } else {
          const journals = grouped[year];
          if (state.journal + 1 < journals.length) {
              nextSrc = journals[state.journal + 1].pages[0];
          }
      }

      if (nextSrc && preloadImage) preloadImage.src = nextSrc;
  }

});
